import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PatientListResponse,
  PatientPageResponse,
  QuickFactsAndGoalsListResponse,
  QuickFactsAndGoalsSaveRequest,
} from "../../../shared/api-types";
import { authenticatedApi } from "@/lib/api";
import { patientPageQueryKey } from "@/lib/patient-page-client";
import { PATIENTS_QUERY_KEY } from "@/lib/patients-client";
import {
  listPath,
  listQueryKey,
  listToCacheAfterFailure,
  saveFailureResult,
  type ListKind,
  type ListSaveResult,
} from "@/lib/quick-facts-and-goals-client";

// The RD's own Patients list (NO-835), from GET /api/patients, and one
// Client's page (NO-849), from GET /api/patients/:patientId. The list carries
// Client names, so its query key holds nothing but a constant; the page's key
// holds only the Client id. Only the pages' PatientsGate mounts a caller, and
// only once the page may show, so a viewer the flag hides never asks.

export function usePatientList() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: PATIENTS_QUERY_KEY,
    queryFn: async () =>
      (await authenticatedApi<{ data: PatientListResponse }>(getToken, "/api/patients"))
        .data,
  });
}

/**
 * Own lane only (no `?rdId=`). `gcTime: 0` drops the SOAP excerpts and the
 * Quick Note from the cache as soon as the page unmounts.
 */
export function usePatientPage(patientId: string) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: patientPageQueryKey(patientId),
    queryFn: async () =>
      (
        await authenticatedApi<{ data: PatientPageResponse }>(
          getToken,
          `/api/patients/${encodeURIComponent(patientId)}`
        )
      ).data,
    gcTime: 0,
  });
}

/**
 * NO-852: one Client's Quick facts or Goals, own lane only. `enabled` is the
 * viewer's patient_quick_notes:read:own, so a viewer without it never asks.
 * The key holds only the Client id; `gcTime: 0` drops the list on unmount.
 */
export function useQuickFactsOrGoals(
  kind: ListKind,
  patientId: string,
  enabled: boolean
) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: listQueryKey(kind, patientId),
    queryFn: async () =>
      (
        await authenticatedApi<{ data: QuickFactsAndGoalsListResponse }>(
          getToken,
          listPath(kind, patientId)
        )
      ).data,
    enabled,
    gcTime: 0,
  });
}

/**
 * NO-852: saves one list by compare-and-set. A saved list replaces the
 * cached one; a 409 answers the current list for the card's conflict choice
 * and never retries on its own.
 */
export function useSaveQuickFactsOrGoals(
  kind: ListKind,
  patientId: string
): (items: string[], baseVersion: number) => Promise<ListSaveResult> {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = listQueryKey(kind, patientId);
  const mutation = useMutation({
    mutationFn: async (body: QuickFactsAndGoalsSaveRequest) =>
      (
        await authenticatedApi<{ data: QuickFactsAndGoalsListResponse }>(
          getToken,
          listPath(kind, patientId),
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        )
      ).data,
  });

  return async (items, baseVersion) => {
    try {
      queryClient.setQueryData(
        queryKey,
        await mutation.mutateAsync({ items, baseVersion })
      );

      return { status: "saved" };
    } catch (error) {
      const result = saveFailureResult(error);
      const latest = listToCacheAfterFailure(result);
      if (latest) queryClient.setQueryData(queryKey, latest);

      return result;
    }
  };
}
